import { Component, computed, inject, OnInit, OnDestroy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ActivatedRoute, Router } from '@angular/router';
import { DateTime } from 'luxon';

import {
  AuthStore,
  CancelButtonComponent,
  ErrorAlertComponent,
  FeatureHeaderComponent,
  PERMISSIONS,
} from '@sandbox-platform/shared-ui';

import {
  CommissionPaymentDetailStore,
  CommissionPaymentDetailStoreType,
} from './+state/commission-payment-detail.store';
import {
  PAYMENT_METHOD_LABELS,
  PAYMENT_TYPE_LABELS,
} from '../../../types/commission-payment.types';

@Component({
  standalone: true,
  imports: [
    RouterLink,
    CancelButtonComponent,
    ErrorAlertComponent,
    FeatureHeaderComponent,
  ],
  selector: 'lib-commission-payment-detail',
  templateUrl: './commission-payment-detail.component.html',
  host: { class: 'block h-full overflow-y-auto' },
})
export class CommissionPaymentDetailComponent implements OnInit, OnDestroy {
  protected readonly store: CommissionPaymentDetailStoreType = inject(
    CommissionPaymentDetailStore
  );
  protected readonly authStore = inject(AuthStore);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  protected readonly paymentId = this.route.snapshot.paramMap.get('id');

  protected readonly canView = computed(() =>
    this.authStore.hasExpertPermission(
      PERMISSIONS.EXPERT.COMMISSION_PAYMENT_READ
    )
  );

  async ngOnInit(): Promise<void> {
    this.store.reset();
    if (this.paymentId) {
      await this.store.loadPayment(this.paymentId);
    }
  }

  ngOnDestroy(): void {
    this.store.reset();
  }

  protected goBack(): void {
    this.router.navigate(['/commission-payments']);
  }

  protected paymentTypeLabel(type: string): string {
    return PAYMENT_TYPE_LABELS[type as keyof typeof PAYMENT_TYPE_LABELS] ?? type;
  }

  protected paymentTypeClass(type: string): string {
    switch (type) {
      case 'payment':
        return 'bg-green-100 text-green-800';
      case 'reversal':
        return 'bg-red-100 text-red-800';
      case 'adjustment':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  protected paymentMethodLabel(method: string): string {
    return PAYMENT_METHOD_LABELS[method as keyof typeof PAYMENT_METHOD_LABELS] ?? method;
  }

  protected formatDate(date?: string | Date): string {
    if (!date) return '-';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (Number.isNaN(dateObj.getTime()) || dateObj.getFullYear() <= 1) return '-';
    return DateTime.fromJSDate(dateObj).toFormat('dd-MMM-yyyy');
  }

  protected formatDateTime(date?: string | Date): string {
    if (!date) return '-';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (Number.isNaN(dateObj.getTime()) || dateObj.getFullYear() <= 1) return '-';
    return DateTime.fromJSDate(dateObj).toFormat('dd-MMM-yyyy HH:mm');
  }

  protected formatMoney(amountMinor?: number, currency = 'KES'): string {
    if (amountMinor === undefined || amountMinor === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amountMinor / 100);
  }
}
